import React from "react";
import classes from "./AiSummaryBiologicalAgeCard.module.css";

type Props = {
  biologicalAge?: number;
  chronologicalAge?: number;
  description?: string;
  variant?: "screen" | "pdf";
};

const clampAge = (value: number): number => Math.max(0, Math.min(100, value));

const formatNumber = (value: number): string =>
  new Intl.NumberFormat(undefined, {
    maximumFractionDigits: Number.isInteger(value) ? 0 : 1,
  }).format(value);

const getLabelPoint = (
  value: number,
  horizontalOffset: number
): { x: number; y: number } => {
  const angle = Math.PI - (clampAge(value) / 100) * Math.PI;
  return {
    x: 100 + Math.cos(angle) * 58 + horizontalOffset,
    y: 100 - Math.sin(angle) * 58,
  };
};

const AiSummaryBiologicalAgeCard = ({
  biologicalAge,
  chronologicalAge,
  description,
  variant = "screen",
}: Props) => {
  if (biologicalAge === undefined && chronologicalAge === undefined) {
    return null;
  }

  const difference =
    biologicalAge !== undefined && chronologicalAge !== undefined
      ? chronologicalAge - biologicalAge
      : undefined;
  const differenceLabel =
    difference === undefined
      ? ""
      : difference > 0
      ? `${formatNumber(difference)} yrs younger`
      : difference < 0
      ? `${formatNumber(Math.abs(difference))} yrs older`
      : "Matches chronological age";
  const biologicalProgress = clampAge(biologicalAge ?? 0);
  const chronologicalProgress = clampAge(chronologicalAge ?? 0);
  const biologicalPoint =
    biologicalAge !== undefined ? getLabelPoint(biologicalAge, -9) : null;
  const chronologicalPoint =
    chronologicalAge !== undefined ? getLabelPoint(chronologicalAge, 9) : null;
  const accessibleLabel = [
    biologicalAge !== undefined
      ? `Biological age ${formatNumber(biologicalAge)}`
      : "",
    chronologicalAge !== undefined
      ? `chronological age ${formatNumber(chronologicalAge)}`
      : "",
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <article
      className={`${classes.card} ${variant === "pdf" ? classes.pdf : ""}`}
    >
      <div className={classes.contentRow}>
        <div className={classes.ageIdentity}>
          <p className={classes.eyebrow}>Biological age</p>
          <div className={classes.headline}>
            {biologicalAge !== undefined && (
              <strong>{formatNumber(biologicalAge)}</strong>
            )}
            {differenceLabel && (
              <span
                className={
                  difference !== undefined && difference < 0
                    ? classes.attentionTag
                    : classes.positiveTag
                }
              >
                {differenceLabel}
              </span>
            )}
          </div>
        </div>

        <svg
          className={classes.gauge}
          viewBox="0 0 200 116"
          role="img"
          aria-label={accessibleLabel}
        >
          <path
            className={classes.track}
            d="M 22 100 A 78 78 0 0 1 178 100"
            pathLength="100"
          />
          {chronologicalAge !== undefined && (
            <path
              className={classes.chronologicalArc}
              d="M 22 100 A 78 78 0 0 1 178 100"
              pathLength="100"
              strokeDasharray={`${chronologicalProgress} ${
                100 - chronologicalProgress
              }`}
            />
          )}
          {biologicalAge !== undefined && (
            <path
              className={classes.biologicalArc}
              d="M 22 100 A 78 78 0 0 1 178 100"
              pathLength="100"
              strokeDasharray={`${biologicalProgress} ${
                100 - biologicalProgress
              }`}
            />
          )}
          {chronologicalAge !== undefined && (
            <line
              className={classes.chronologicalNeedle}
              x1="100"
              y1="100"
              x2="100"
              y2="42"
              style={{
                transform: `rotate(${chronologicalProgress * 1.8 - 90}deg)`,
              }}
            />
          )}
          {biologicalAge !== undefined && (
            <line
              className={classes.biologicalNeedle}
              x1="100"
              y1="100"
              x2="100"
              y2="34"
              style={{
                transform: `rotate(${biologicalProgress * 1.8 - 90}deg)`,
              }}
            />
          )}
          <circle className={classes.gaugeHub} cx="100" cy="100" r="6" />
          <text className={classes.scaleLabel} x="22" y="114">
            0
          </text>
          <text className={classes.scaleLabel} x="178" y="114" textAnchor="end">
            100
          </text>
          {chronologicalAge !== undefined && chronologicalPoint && (
            <text
              className={classes.chronologicalLabel}
              x={chronologicalPoint.x}
              y={chronologicalPoint.y}
              textAnchor="middle"
            >
              {formatNumber(chronologicalAge)}
            </text>
          )}
          {biologicalAge !== undefined && biologicalPoint && (
            <text
              className={classes.biologicalLabel}
              x={biologicalPoint.x}
              y={biologicalPoint.y}
              textAnchor="middle"
            >
              {formatNumber(biologicalAge)}
            </text>
          )}
        </svg>
      </div>

      <div className={classes.legend}>
        {chronologicalAge !== undefined && (
          <span>
            <i className={classes.chronologicalDot} /> Chronological{" "}
            {formatNumber(chronologicalAge)}
          </span>
        )}
        {biologicalAge !== undefined && (
          <span>
            <i className={classes.biologicalDot} /> Biological{" "}
            {formatNumber(biologicalAge)}
          </span>
        )}
      </div>
      {description && <p className={classes.description}>{description}</p>}
    </article>
  );
};

export default AiSummaryBiologicalAgeCard;
